import { InfoCircleOutlined } from "@ant-design/icons";
import { Flex, Modal } from "antd";
import dayjs from "dayjs";
import { FC, useEffect } from "react";
import {
    getKpiGetProgressThunk,
    getKpiGetSalaryToPayThunk,
    getMotivationInfoThunk,
    postMessengerReceivedDelivery,
} from "tm-shared/api";
import { UserRole } from "tm-shared/types";

import { ProgressLoader } from "@/app/ProgressLoader";
import BaseButton from "@/components/BaseButton";
import { PrimaryText, SecondaryText } from "@/components/Typography/Styled";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { useAsyncTaskQueue } from "@/hooks/useAsyncTaskQueue";
import { setShouldUpdateKpi } from "@/store/slices/offline";
import {
    getKpiGetProgressSvThunk,
    getKpiGetSalaryToPaySvThunk,
} from "@/store/slices/sv";
import { ModalPayloads, ModalType } from "@/types/modals";
import { getKpiTargetDate } from "@/utils/business/getKpiTargetDate";

export interface KpiProgressModalProps {
    payload: ModalPayloads[ModalType.KpiProgress];
    onCloseModal: () => void;
}

const KpiProgressModal: FC<KpiProgressModalProps> = ({
    payload,
    onCloseModal,
}) => {
    const dispatch = useAppDispatch();
    const { latency } = useAppSelector((s) => s.offline);
    const { motivation } = useAppSelector((s) => s.work);
    const { id: userId, role: userRole } = useAppSelector((s) => s.user);

    const { queue, progress } = useAsyncTaskQueue();

    const isOldData =
        motivation?.calculatedAt &&
        dayjs(motivation.calculatedAt).isAfter(dayjs(payload?.calculatedAt));

    useEffect(() => {
        if (payload?.deliveryId) {
            postMessengerReceivedDelivery({
                deliveryId: payload?.deliveryId,
            });
        }
    }, [payload?.deliveryId]);

    useEffect(() => {
        if (isOldData) {
            onCloseModal();
        }
    }, [onCloseModal, isOldData]);

    const handleConfirm = async () => {
        const commonKpiParams = {
            employeeId: userId,
            targetDate: getKpiTargetDate(),
        };

        queue.add("Завантаження зарплати", () =>
            dispatch(
                userRole === UserRole.Sv
                    ? getKpiGetSalaryToPaySvThunk(commonKpiParams)
                    : getKpiGetSalaryToPayThunk(commonKpiParams),
            ).unwrap(),
        );

        queue.add("Завантаження прогресу KPI", () =>
            dispatch(
                userRole === UserRole.Sv
                    ? getKpiGetProgressSvThunk(commonKpiParams)
                    : getKpiGetProgressThunk(commonKpiParams),
            ).unwrap(),
        );

        queue.add("Завантаження інформації про мотивацію", () =>
            dispatch(getMotivationInfoThunk({ employeeId: userId })).unwrap(),
        );

        try {
            await queue.runParallel();
        } catch (error) {
            console.error("Init error:", error);
        } finally {
            setTimeout(() => {
                queue.clear();

                onCloseModal();
            }, 2000);
        }
    };

    const handleCancel = () => {
        dispatch(setShouldUpdateKpi(true));
        onCloseModal();
    };

    if (isOldData) {
        return null;
    }

    const isLoaderOpen = progress.isRunning || progress.tasks.length > 0;

    const isStableInternet =
        typeof latency === "string" ? false : latency < 1000;

    return (
        <>
            <Modal open title="" closable={false} centered footer={[]}>
                <Flex vertical gap={16}>
                    <PrimaryText
                        style={{ textAlign: "center" }}
                        fontSize={20}
                        fontWeight="500"
                    >
                        Дані KPI оновлено
                    </PrimaryText>
                    <SecondaryText>
                        Оновити інформацію на дашборді зараз?
                    </SecondaryText>

                    {!isStableInternet && (
                        <Flex gap={8}>
                            <InfoCircleOutlined style={{ fontSize: 20 }} />
                            <SecondaryText>
                                Оновити дані можливо лише за наявності
                                стабільного інтернет з&apos;єднання
                            </SecondaryText>
                        </Flex>
                    )}

                    <Flex vertical gap={8}>
                        <BaseButton
                            type="primary"
                            onClick={handleConfirm}
                            disabled={!isStableInternet}
                        >
                            Оновити зараз
                        </BaseButton>

                        <BaseButton type="default" onClick={handleCancel}>
                            Оновити пізніше
                        </BaseButton>
                    </Flex>
                </Flex>
            </Modal>

            <ProgressLoader open={isLoaderOpen} tasks={progress.tasks} />
        </>
    );
};

export default KpiProgressModal;
